import { SpanStatusCode, context, trace } from "@opentelemetry/api";
import type { Middleware } from "redux";

export interface ReduxTracingConfig {
  include?: string[]; // action type patterns to trace (glob-style, default: ['*'])
  exclude?: string[]; // action type patterns to skip
}

const DEFAULT_EXCLUDE = [
  "SET_EVALUATED_TREE",
  "SET_META_PROP",
  "SET_WIDGET_LOADING",
];

/**
 * Returns true if the pattern matches the action type.
 * Supports simple glob: '*' matches everything, 'prefix/*' matches anything
 * starting with 'prefix/'.
 */
function matchesPattern(pattern: string, actionType: string): boolean {
  if (pattern === "*") return true;
  if (pattern.endsWith("/*")) {
    const prefix = pattern.slice(0, -2);
    return actionType.startsWith(prefix + "/");
  }
  return pattern === actionType;
}

function shouldTrace(
  actionType: string,
  include: string[],
  exclude: string[],
): boolean {
  const isExcluded = exclude.some((pattern) =>
    matchesPattern(pattern, actionType),
  );
  if (isExcluded) return false;

  const isIncluded = include.some((pattern) =>
    matchesPattern(pattern, actionType),
  );
  return isIncluded;
}

export function createReduxTracingMiddleware(
  config?: ReduxTracingConfig,
): Middleware {
  const include = config?.include ?? ["*"];
  const exclude = config?.exclude ?? DEFAULT_EXCLUDE;

  return (_store) => (next) => (action) => {
    const actionType =
      action != null && typeof action === "object" && "type" in action
        ? String((action as { type: unknown }).type)
        : undefined;

    if (!actionType || !shouldTrace(actionType, include, exclude)) {
      return next(action);
    }

    const tracer = trace.getTracer("redux");
    const span = tracer.startSpan(`redux.action.${actionType}`);
    span.setAttribute("redux.action.type", actionType);

    return context.with(trace.setSpan(context.active(), span), () => {
      try {
        const result = next(action);
        span.end();
        return result;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        span.recordException(err);
        span.setStatus({ code: SpanStatusCode.ERROR, message: err.message });
        span.end();
        throw error;
      }
    });
  };
}
